import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from "@facade/components/ui";
import { CircleAlert, Download, FileCheck2 } from "lucide-react";
import { isJobActive, jobQueries } from "../queries";
import { useQuery } from "@tanstack/react-query";

import { JobProgressDetails } from "./JobProgressDetails";
import { useDownloadJobResultMutation } from "../mutations";
import { useTranslation } from "react-i18next";

type JobResultDialogProps = {
  jobId: string | null;
  onClose: () => void;
};

export function JobResultDialog({ jobId, onClose }: JobResultDialogProps) {
  const { t } = useTranslation();
  const jobQuery = useQuery({
    ...jobQueries.detail(jobId ?? ""),
    enabled: Boolean(jobId),
  });
  const download = useDownloadJobResultMutation();
  const job = jobQuery.data;
  const active = job ? isJobActive(job) : false;
  const issueCount = Number(job?.result?.issue_count ?? 0);
  const thumbnailFailed =
    job?.type === "IFC_GEOMETRY" &&
    (job.result?.thumbnail as { status?: string } | undefined)?.status ===
      "FAILED";

  return (
    <Dialog open={Boolean(jobId)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(30rem,calc(100vw-2rem))]">
        {jobQuery.isPending ? (
          <div className="flex min-h-28 items-center justify-center gap-2 text-muted-foreground">
            <Spinner />
            {t("jobs.details.loading")}
          </div>
        ) : jobQuery.isError || !job ? (
          <>
            <DialogHeader>
              <CircleAlert className="mb-2 size-8 text-destructive" />
              <DialogTitle>{t("jobs.details.loadErrorTitle")}</DialogTitle>
              <DialogDescription>
                {t("jobs.details.loadErrorDescription")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t("actions.close")}
              </Button>
            </DialogFooter>
          </>
        ) : active ? (
          <DialogHeader>
            <Spinner className="mb-2 size-8 text-primary" />
            <DialogTitle>
              {job.type === "REPORT_PDF"
                ? t("jobs.report.progressTitle")
                : job.type === "IFC_GEOMETRY"
                  ? t("jobs.ifc.progressTitle")
                  : job.type === "IFC_FEDERATION_THUMBNAIL"
                    ? t("jobs.federationThumbnail.progressTitle")
                    : job.type === "BCF_IMPORT"
                      ? t("jobs.bcf.progressTitle")
                      : t("jobs.generic.progressTitle")}
            </DialogTitle>
            <DialogDescription>
              {job.type === "REPORT_PDF"
                ? t("jobs.report.progressDescription")
                : job.type === "IFC_GEOMETRY"
                  ? job.progress?.phase === "IFC_THUMBNAIL"
                    ? t("jobs.ifc.thumbnailDescription")
                    : t("jobs.ifc.progressDescription")
                  : job.type === "IFC_FEDERATION_THUMBNAIL"
                    ? t("jobs.federationThumbnail.progressDescription")
                    : t("jobs.processingDescription")}
            </DialogDescription>
            <JobProgressDetails job={job} />
          </DialogHeader>
        ) : job.status === "SUCCEEDED" && job.type === "REPORT_PDF" ? (
          <>
            <DialogHeader>
              <div className="mb-2 grid size-10 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
                <FileCheck2 className="size-5" />
              </div>
              <DialogTitle>{t("jobs.report.readyTitle")}</DialogTitle>
              <DialogDescription>
                {t("jobs.report.readyDescription", { count: issueCount })}
              </DialogDescription>
            </DialogHeader>

            {download.isError ? (
              <p className="text-xs text-destructive">
                {t("jobs.report.downloadError")}
              </p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t("actions.close")}
              </Button>
              <Button
                type="button"
                disabled={download.isPending}
                onClick={() => download.mutate(job.id)}
              >
                {download.isPending ? <Spinner /> : <Download />}
                {t("jobs.report.download")}
              </Button>
            </DialogFooter>
          </>
        ) : job.status === "SUCCEEDED" ? (
          <>
            <DialogHeader>
              <div className="mb-2 grid size-10 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
                <FileCheck2 className="size-5" />
              </div>
              <DialogTitle>
                {job.type === "IFC_GEOMETRY"
                  ? t("jobs.ifc.readyTitle")
                  : job.type === "IFC_FEDERATION_THUMBNAIL"
                    ? t("jobs.federationThumbnail.readyTitle")
                    : job.type === "BCF_IMPORT"
                      ? t("jobs.bcf.readyTitle")
                      : t("jobs.generic.readyTitle")}
              </DialogTitle>
              <DialogDescription>
                {job.type === "IFC_GEOMETRY"
                  ? thumbnailFailed
                    ? t("jobs.ifc.readyWithPreviewWarning")
                    : t("jobs.ifc.readyDescription", {
                        count: Number(job.result?.processed_entities ?? 0),
                      })
                  : job.type === "IFC_FEDERATION_THUMBNAIL"
                    ? t("jobs.federationThumbnail.readyDescription")
                    : job.type === "BCF_IMPORT"
                      ? t("jobs.bcf.readyDescription")
                      : t("jobs.generic.readyDescription")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t("actions.close")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <CircleAlert className="mb-2 size-8 text-destructive" />
              <DialogTitle>{t("jobs.details.failedTitle")}</DialogTitle>
              <DialogDescription>
                {t("jobs.details.failedDescription")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t("actions.close")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
